import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { getCurrentUserProfile, updateUserProfile } from '../services/userProfile';
import { syncGoogleAvatar } from '../services/avatar';
import type { UserProfile } from '../types/user';

// ============================================
// TYPES
// ============================================

interface Household {
  id: string;
  name: string;
  role: string;
}

interface AuthContextValue {
  // Auth state
  user: User | null;
  session: Session | null;
  loading: boolean;

  // User profile
  userProfile: UserProfile | null;
  updateProfile: (updates: Partial<UserProfile>) => Promise<boolean>;
  refreshProfile: () => Promise<void>;

  // Household state
  currentHousehold: Household | null;
  households: Household[];

  // Auth actions
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;

  // Household actions
  switchHousehold: (householdId: string) => void;
  refreshHouseholds: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [currentHousehold, setCurrentHousehold] = useState<Household | null>(null);

  // Fetch user profile
  const refreshProfile = useCallback(async () => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    const profile = await getCurrentUserProfile();
    setUserProfile(profile);
  }, [user]);

  // Update user profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>): Promise<boolean> => {
    const updated = await updateUserProfile(updates);
    if (updated) {
      setUserProfile(updated);
      return true;
    }
    return false;
  }, []);

  // Fetch user's households
  const refreshHouseholds = useCallback(async () => {
    if (!user) {
      setHouseholds([]);
      setCurrentHousehold(null);
      return;
    }

    const { data, error } = await supabase
      .rpc('get_user_households', { p_user_id: user.id });

    if (error) {
      console.error('Failed to fetch households:', error);
      return;
    }

    const householdList: Household[] = (data || []).map((h: any) => ({
      id: h.household_id,
      name: h.household_name,
      role: h.role,
    }));

    setHouseholds(householdList);

    // Set current household from localStorage or first available
    const savedHouseholdId = localStorage.getItem('currentHouseholdId');
    const savedHousehold = householdList.find(h => h.id === savedHouseholdId);

    if (savedHousehold) {
      setCurrentHousehold(savedHousehold);
    } else if (householdList.length > 0) {
      setCurrentHousehold(householdList[0]);
      localStorage.setItem('currentHouseholdId', householdList[0].id);
    }
  }, [user]);

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle Google SSO - sync avatar to storage on login
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
          const googleAvatarUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture;
          const provider = session.user.app_metadata?.provider;

          // If signed in with Google and has avatar URL, sync to storage
          if (provider === 'google' && googleAvatarUrl) {
            try {
              // Get current profile to check if we need to sync
              const currentProfile = await getCurrentUserProfile();
              const storedGoogleSource = currentProfile?.preferences?.google_avatar_source;
              const currentAvatarUrl = currentProfile?.avatar_url;

              // Sync only if changed or never synced
              const syncResult = await syncGoogleAvatar(
                session.user.id,
                googleAvatarUrl,
                storedGoogleSource,
                currentAvatarUrl
              );

              if (syncResult) {
                // Update profile with new avatar URL and store the Google source for future comparison
                await updateUserProfile({
                  avatar_url: syncResult.avatarUrl,
                  preferences: {
                    ...currentProfile?.preferences,
                    google_avatar_source: syncResult.googleSource,
                  },
                });
              }
            } catch (err) {
              console.warn('Failed to sync Google avatar:', err);
            }
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Fetch data when user changes
  useEffect(() => {
    if (user) {
      refreshHouseholds();
      refreshProfile();
    } else {
      setHouseholds([]);
      setCurrentHousehold(null);
      setUserProfile(null);
    }
  }, [user, refreshHouseholds, refreshProfile]);

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  };

  // Sign up with email/password
  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  };

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('currentHouseholdId');
    setHouseholds([]);
    setCurrentHousehold(null);
    setUserProfile(null);
  };

  // Switch active household
  const switchHousehold = (householdId: string) => {
    const household = households.find(h => h.id === householdId);
    if (household) {
      setCurrentHousehold(household);
      localStorage.setItem('currentHouseholdId', householdId);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        userProfile,
        updateProfile,
        refreshProfile,
        currentHousehold,
        households,
        signIn,
        signUp,
        signOut,
        switchHousehold,
        refreshHouseholds,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// HOOKS
// ============================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Convenience hook for just the household ID
export function useHouseholdId(): string | null {
  const { currentHousehold } = useAuth();
  return currentHousehold?.id ?? null;
}

// Convenience hook for user profile
export function useUserProfile() {
  const { userProfile, updateProfile, refreshProfile } = useAuth();
  return { userProfile, updateProfile, refreshProfile };
}
